import { useId, useRef, useState } from 'react'
import { ListFilter, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { ErrorState } from '../../components/States'
import { useToast } from '../../components/toastContext'
import { serverDetail, serverText } from '../../lib/i18n'
import { SETTING_KEYS, savePhishingSetting } from './phishingService'
import { isHeaderName, readHeaderRules, serializeHeaderRules } from './phishingHeaderRules'
import { phishingSettingsStrings as S } from './strings'
import TitleWithHint from './TitleWithHint'

const C = S.settings

export default function IgnoredHeadersPanel({ value, onChange }) {
  const formId = useId()
  const toast = useToast()
  const [initial] = useState(() => readHeaderRules(value))
  const [rules, setRules] = useState(() => (initial ?? []).map((rule, id) => ({ ...rule, id })))
  const nextId = useRef(rules.length)
  const inFlight = useRef(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})
  const dirty = initial !== null && serializeHeaderRules(rules) !== serializeHeaderRules(initial)

  const update = (id, field, next) => {
    setRules((previous) => previous.map((rule) => rule.id === id ? { ...rule, [field]: next } : rule))
    setErrors({})
  }

  const save = async (event) => {
    event.preventDefault()
    if (inFlight.current || !dirty || initial === null) return
    const nextErrors = {}
    const seen = new Set()
    for (const rule of rules) {
      const fields = {}
      if (!isHeaderName(rule.key)) fields.key = C.headerNameInvalid
      if (['\r', '\n', '\0'].some((character) => rule.value.includes(character))) fields.value = C.headerValueInvalid
      const signature = JSON.stringify([rule.key.trim().toLowerCase(), rule.value, rule.isExclude])
      if (seen.has(signature)) fields.key = C.headerDuplicate
      if (Object.keys(fields).length) nextErrors[rule.id] = fields
      seen.add(signature)
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return
    inFlight.current = true
    setSaving(true)
    try {
      const serialized = serializeHeaderRules(rules)
      const outcome = await savePhishingSetting(SETTING_KEYS.skipHeaders, serialized)
      if (!outcome.ok) {
        toast.error(serverDetail(outcome.message) || S.settingsFail.headers)
        return
      }
      onChange(serialized)
      toast.success(serverText(outcome.message) || S.headersSaved)
    } catch (error) {
      toast.error(serverDetail(error?.message) || S.settingsFail.headers)
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  /* The legacy elements — the sentence with "add new" beside it, a key / value / type row per
     rule with its own delete, then Save — as a card of their own (2026-10-22): the sentence is
     the title, "add new" its action, Save its footer. */
  return (
    <Card
      className="ph-settings__card ph-settings__wide"
      icon={ListFilter}
      title={<TitleWithHint title={C.skipHeaders} hint={C.skipHeadersHint} />}
      actions={
        initial !== null && (
          <Button type="button" variant="secondary" size="sm" disabled={saving} onClick={() => {
            const id = nextId.current++
            setRules((previous) => [...previous, { id, key: '', value: '', isExclude: true }])
          }}>
            <Plus size={16} strokeWidth={2} />
            {C.skipHeadersAdd}
          </Button>
        )
      }
      footer={
        initial !== null && (
          <Button type="submit" form={formId} busy={saving} disabled={!dirty}>
            {S.save}
          </Button>
        )
      }
    >
      {initial === null ? <ErrorState title={C.headersInvalid} /> : (
        <form id={formId} onSubmit={save} className="ph-settings__rules">
          {rules.length === 0 && <p className="ph-settings__empty">{C.skipHeadersEmpty}</p>}
          {rules.map((rule) => (
            <div className="ph__header-rule" key={rule.id}>
              <FormRow label={C.headerName} error={errors[rule.id]?.key}>
                {(id) => <TextField id={id} dir="ltr" value={rule.key} disabled={saving}
                  invalid={!!errors[rule.id]?.key} autoComplete="off" spellCheck={false}
                  onChange={(next) => update(rule.id, 'key', next)} />}
              </FormRow>
              <FormRow label={C.headerValue} error={errors[rule.id]?.value}>
                {(id) => <TextField id={id} dir="ltr" value={rule.value} disabled={saving}
                  invalid={!!errors[rule.id]?.value} autoComplete="off" spellCheck={false}
                  onChange={(next) => update(rule.id, 'value', next)} />}
              </FormRow>
              <FormRow label={C.headerType}>
                {(id) => <Select id={id} value={String(rule.isExclude)} disabled={saving}
                  options={[{ value: 'true', label: C.headerExclude }, { value: 'false', label: C.headerInclude }]}
                  onChange={(next) => update(rule.id, 'isExclude', next === 'true')} />}
              </FormRow>
              <Button variant="danger-secondary" disabled={saving}
                aria-label={`${S.delete}: ${rule.key || C.headerName}`}
                onClick={() => { setRules((previous) => previous.filter((item) => item.id !== rule.id)); setErrors({}) }}>
                <Trash2 size={16} strokeWidth={1.75} />
                {S.delete}
              </Button>
            </div>
          ))}
        </form>
      )}
    </Card>
  )
}
